import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import Delete from "../../components/Atomos/Icons/Delete";
import GrayEye from "../../components/Atomos/Icons/grayEye";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import { AccessTypeWithStaff } from "../../Enums/UserEnums";
import { IPagination2, ISorting } from "../../interfaces/Components/ITable";
import {
  cancelEmailSend,
  getEmailSendListPage,
  IEmailSendListItem,
} from "../../Services/EmailSend";
import { useAuth } from "../../store/AuthContext";
import { toastError, toastPromise } from "../../utils/toast";

type EmailListViewMode = "pending" | "sent";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGINATION: IPagination2 = { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE };
const TABLE_INSTANCE_KEY_PREFIX = "scheduled-email-list";
const EASTERN_TIME_ZONE = "America/New_York";
const DATE_TIME_FORMAT = "MM/dd/yyyy - HH:mm";

const DEFAULT_SORTING_BY_VIEW: Record<EmailListViewMode, ISorting> = {
  pending: { field: "scheduledDateTime", order: "asc" },
  sent: { field: "sentDateTime", order: "desc" },
};

function normalizeTimestampToMs(value?: number): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return Math.floor(value * 1000);
}

function formatScheduledDateTime(row: IEmailSendListItem): string {
  const ms = normalizeTimestampToMs(row.scheduledDateTime);
  const timezoneLabel = resolveTimezoneLabel(row);
  const statusLabel = row.status === "processing" ? "Processing" : "";

  if (!ms) {
    return statusLabel || "—";
  }
  const dateTime = formatInTimeZone(
    new Date(ms),
    EASTERN_TIME_ZONE,
    DATE_TIME_FORMAT,
  );
  return [dateTime, timezoneLabel, statusLabel].filter(Boolean).join(" - ");
}

function getTimezoneTypeLabel(value?: IEmailSendListItem["timezoneType"]): string {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (normalized === "regionaltime" || normalized === "regional") {
    return "Regional";
  }
  if (normalized === "est") {
    return "EST";
  }
  return "";
}

function resolveTimezoneLabel(row: IEmailSendListItem): string {
  const explicitLabel = String(row.timezoneLabel || "").trim();
  if (explicitLabel === "EST" || explicitLabel === "Regional") {
    return explicitLabel;
  }
  return getTimezoneTypeLabel(row.timezoneType);
}

function formatSentDateTime(row: IEmailSendListItem): string {
  const timezoneLabel = resolveTimezoneLabel(row);
  const sentMs = normalizeTimestampToMs(row.sentDateTime);
  const scheduledMs = normalizeTimestampToMs(row.scheduledDateTime);

  if (row.status === "failed") {
    const failureMs = sentMs || scheduledMs;
    if (!failureMs) {
      return "Failed";
    }
    const dateTime = formatInTimeZone(
      new Date(failureMs),
      EASTERN_TIME_ZONE,
      DATE_TIME_FORMAT,
    );
    return timezoneLabel ? `${dateTime} - ${timezoneLabel}` : `${dateTime} - Failed`;
  }

  if (scheduledMs) {
    const dateTime = formatInTimeZone(
      new Date(scheduledMs),
      EASTERN_TIME_ZONE,
      DATE_TIME_FORMAT,
    );
    return timezoneLabel ? `${dateTime} - ${timezoneLabel}` : dateTime;
  }

  if (row.sendType === "scheduled") {
    if (!sentMs) {
      return timezoneLabel || "Scheduled";
    }
    const dateTime = formatInTimeZone(
      new Date(sentMs),
      EASTERN_TIME_ZONE,
      DATE_TIME_FORMAT,
    );
    return timezoneLabel ? `${dateTime} - ${timezoneLabel}` : `${dateTime} - Scheduled`;
  }

  if (!sentMs) {
    return "—";
  }
  return `${formatInTimeZone(new Date(sentMs), EASTERN_TIME_ZONE, DATE_TIME_FORMAT)} - Send Now`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    const data = error.response.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message.trim()
    ) {
      return data.message;
    }
  }
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message.trim()
  ) {
    return error.message;
  }
  return fallback;
}

export default function PendingScheduledEmails(): React.ReactElement {
  const navigate = useNavigate();
  const { tokenDecoded } = useAuth();
  const isSuperStaff =
    tokenDecoded?.extension_WSG_Permissions === AccessTypeWithStaff.wsgSuper;
  const [viewMode, setViewMode] = useState<EmailListViewMode>("pending");
  const [rows, setRows] = useState<IEmailSendListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sorting, setSorting] = useState<ISorting>(DEFAULT_SORTING_BY_VIEW.pending);
  const [pagination, setPagination] = useState<IPagination2>(DEFAULT_PAGINATION);
  const [cancelTarget, setCancelTarget] = useState<IEmailSendListItem | null>(null);
  const [errorTarget, setErrorTarget] = useState<IEmailSendListItem | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchRows = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getEmailSendListPage(
        viewMode === "pending" ? "pendingScheduled" : "sent",
        undefined,
        {
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sortField: sorting.field,
          sortOrder: sorting.order === "asc" ? "asc" : "desc",
        },
      );
      setRows(response.items);
      setTotalCount(response.pagination.totalCount);
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      toastError(
        getErrorMessage(error, "Unable to load scheduled email list."),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    viewMode,
    pagination.pageIndex,
    pagination.pageSize,
    sorting.field,
    sorting.order,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleToggleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const next = prev === "pending" ? "sent" : "pending";
      setSorting(DEFAULT_SORTING_BY_VIEW[next]);
      setPagination(DEFAULT_PAGINATION);
      return next;
    });
  }, []);

  const paginatedRows = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    return rows.map((row, index) => ({
      ...row,
      index: start + index + 1,
    }));
  }, [rows, pagination.pageIndex, pagination.pageSize]);

  const openCancelModal = useCallback(
    (row: IEmailSendListItem) => {
      if (!isSuperStaff || row.status === "processing") {
        return;
      }
      setCancelTarget(row);
    },
    [isSuperStaff],
  );

  const handleSortingChange = useCallback((next: ISorting) => {
    setSorting(next);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const closeCancelModal = useCallback(() => {
    if (isCancelling) {
      return;
    }
    setCancelTarget(null);
  }, [isCancelling]);

  const openErrorModal = useCallback((row: IEmailSendListItem) => {
    setErrorTarget(row);
  }, []);

  const closeErrorModal = useCallback(() => {
    setErrorTarget(null);
  }, []);

  const handleConfirmCancelSend = useCallback(async () => {
    if (!isSuperStaff || !cancelTarget?.id || isCancelling) {
      return;
    }

    setIsCancelling(true);
    try {
      await toastPromise(cancelEmailSend(cancelTarget.id), {
        pending: "Cancelling scheduled email send...",
        success: "Email scheduled send cancelled.",
        error: (error) =>
          getErrorMessage(error, "Unable to cancel the scheduled send."),
      });
      setCancelTarget(null);
      await fetchRows();
    } catch {
      // toastPromise already shows the error state.
    } finally {
      setIsCancelling(false);
    }
  }, [cancelTarget, fetchRows, isCancelling, isSuperStaff]);

  const pendingColumns = useMemo(() => {
    const columns = [
      { id: "index", header: "#", width: 68, enableSorting: false },
      {
        id: "scheduledDateTime",
        header: "Scheduled Date / Time",
        width: 260,
        cell: ({ row }: { row: IEmailSendListItem }) =>
          formatScheduledDateTime(row),
      },
      { id: "scheduledBy", header: "Scheduled By", width: 220 },
      { id: "listName", header: "List", width: 320 },
      { id: "recipientCount", header: "# of Recipients", width: 190 },
      { id: "emailNumber", header: "Email #", width: 120, enableSorting: false },
    ];

    if (!isSuperStaff) {
      return columns;
    }

    return [
      ...columns,
      {
        id: "actions",
        header: "Action",
        enableSorting: false,
        width: 140,
        cell: ({ row }: { row: IEmailSendListItem }) =>
          row.status === "processing" ? (
            <span className="text-gray-400">Processing</span>
          ) : (
            <button
              type="button"
              onClick={() => openCancelModal(row)}
              className="inline-flex items-center gap-2 text-gray-font hover:text-feedback-error-dark transition-colors"
            >
              <span>Cancel</span>
              <Delete className="w-4 h-4" />
            </button>
          ),
      },
    ];
  }, [isSuperStaff, openCancelModal]);

  const sentColumns = useMemo(
    () => [
      { id: "index", header: "#", width: 68, enableSorting: false },
      {
        id: "sentDateTime",
        header: "Sent Date / Time",
        width: 260,
        cell: ({ row }: { row: IEmailSendListItem }) =>
          formatSentDateTime(row),
      },
      { id: "scheduledBy", header: "Sent/Scheduled By", width: 240 },
      { id: "listName", header: "List", width: 320 },
      { id: "recipientCount", header: "# of Recipients", width: 190 },
      { id: "emailNumber", header: "Email #", width: 120, enableSorting: false },
      {
        id: "actions",
        header: "Action",
        enableSorting: false,
        width: 260,
        cell: ({ row }: { row: IEmailSendListItem }) => (
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              onClick={() => row.templateId && navigate(`/email-design/${row.templateId}`)}
              disabled={!row.templateId}
              className="inline-flex items-center gap-2 text-gray-font hover:text-accent-Default disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>See Template</span>
              <GrayEye className="w-4 h-4" />
            </button>
            {row.status === "failed" && row.errorDetails ? (
              <button
                type="button"
                onClick={() => openErrorModal(row)}
                className="inline-flex items-center text-feedback-error-dark hover:text-feedback-error transition-colors"
              >
                <span>Error details</span>
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [navigate, openErrorModal],
  );

  const dataForTable = useMemo(
    () =>
      paginatedRows.map((row) => ({
        ...row,
        scheduledBy: row.scheduledBy || "—",
        listName: row.listName || "—",
        emailNumber: row.emailNumber || "—",
      })),
    [paginatedRows],
  );

  const emptyStateMessage = isLoading
    ? "Loading emails..."
    : viewMode === "pending"
    ? "No pending scheduled emails found."
    : "No sent emails found.";
  const tableInstanceKey = `${TABLE_INSTANCE_KEY_PREFIX}-${viewMode}`;
  const getRowClassName = useCallback(
    (row: IEmailSendListItem) =>
      row.status === "failed"
        ? "bg-red-50 even:bg-red-50 text-feedback-error-dark"
        : row.status === "processing"
          ? "bg-amber-50 even:bg-amber-50 text-gray-font"
          : "even:bg-gray-100 text-gray-font",
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-white border border-gray-200 p-6 lg:p-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-3xl font-medium text-Default font-decimal">
              {viewMode === "pending" ? "Pending Scheduled Email" : "Sent Email List"}
            </h2>
            <LegacyButton
              text={
                viewMode === "pending"
                  ? "SENT EMAIL LIST"
                  : "PENDING SCHEDULED EMAIL LIST"
              }
              style="filled"
              color="accent"
              content="text"
              size="small"
              onClick={handleToggleViewMode}
            />
          </div>

          <PaginatedTable
            key={tableInstanceKey}
            data={dataForTable}
            totalCount={totalCount}
            columnsDef={viewMode === "pending" ? pendingColumns : sentColumns}
            sorting={sorting}
            pagination={pagination}
            endpoint="emailSend"
            onSortingChange={handleSortingChange}
            onPaginationChange={setPagination}
            showColumnVisibility={false}
            showExcelExport={false}
            emptyStateMessage={emptyStateMessage}
            tableKey={tableInstanceKey}
            getRowClassName={getRowClassName}
          />
        </div>
      </div>

      {isSuperStaff && cancelTarget && (
        <div className="fixed inset-0 z-[1000] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-80"
            onClick={closeCancelModal}
          />

          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 className="text-xl font-decimal font-medium text-Default mb-4">
                Discard scheduled send?
              </h2>

              <p className="text-gray-font mb-6">
                The email [{cancelTarget.listName || "Recipient List"}] is set to
                go out at [{formatScheduledDateTime(cancelTarget)}]. If
                you cancel the scheduled send, the message won&apos;t be delivered.
              </p>

              <div className="flex justify-end gap-4">
                <LegacyButton
                  text="KEEP SCHEDULED"
                  style="outlined"
                  color="accent"
                  content="text"
                  size="small"
                  onClick={closeCancelModal}
                  disabled={isCancelling}
                />
                <LegacyButton
                  text={isCancelling ? "CANCELING..." : "CANCEL SEND"}
                  style="filled"
                  color="delete"
                  content="text"
                  size="small"
                  onClick={handleConfirmCancelSend}
                  disabled={isCancelling}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {errorTarget && (
        <div className="fixed inset-0 z-[1000] overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-80"
            onClick={closeErrorModal}
          />

          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
              onClick={(event) => event.stopPropagation()}
            >
              <h2 className="text-xl font-decimal font-medium text-Default mb-4">
                Error details
              </h2>

              <p className="text-gray-font mb-4">
                The email [{errorTarget.listName || "Recipient List"}] couldn&apos;t be
                send due following:
              </p>

              <div className="bg-red-50 border border-red-200 rounded-md p-4 text-feedback-error-dark whitespace-pre-wrap break-words mb-6">
                {errorTarget.errorDetails || "No error details available."}
              </div>

              <div className="flex justify-end">
                <LegacyButton
                  text="CLOSE"
                  style="filled"
                  color="accent"
                  content="text"
                  size="small"
                  onClick={closeErrorModal}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
